import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, UserPlus, Filter, Building2, Users, Briefcase, Phone, MoreVertical, Trash2, Edit, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import AdminLayout from "@/components/AdminLayout";
import apiClient, { crmApi } from "@/utils/api";
import { toast } from "sonner";

const ContactsList = () => {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(searchParams.get("type") || "");
  const [filterStage, setFilterStage] = useState(searchParams.get("stage") || "");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.contact_type = filterType;
      if (filterStage) params.stage_id = filterStage;
      if (searchQuery) params.search = searchQuery;

      const [contactsRes, stagesRes] = await Promise.all([
        crmApi.getContacts(params),
        crmApi.getStages()
      ]);
      
      setContacts(contactsRes.data);
      setStages(stagesRes.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStage, searchQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => { e.preventDefault(); fetchData(); };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try {
      await crmApi.deleteContact(id);
      setContacts(contacts.filter(c => c.id !== id));
    } catch (error) { /* Error handled silently */ }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/crm/contacts/export/csv', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contactos_ikonic_deals.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contactos exportados exitosamente");
    } catch (error) {
      toast.error("Error al exportar contactos");
    } finally {
      setExporting(false);
    }
  };

  const getIcon = (type) => {
    if (type === "seller") return <Building2 size={16} className="text-emerald-500" />;
    if (type === "buyer") return <Users size={16} className="text-blue-500" />;
    return <Briefcase size={16} className="text-purple-500" />;
  };

  const getLabel = (type) => type === "seller" ? "Vendedor" : type === "buyer" ? "Comprador" : "Corredor";
  const getStage = (stageId) => stages.find(s => s.id === stageId);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="contacts-list">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-white">Contactos</h1>
            <p className="text-[#A3A3A3] text-sm">{contacts.length} contactos encontrados</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleExportCSV}
              disabled={exporting || contacts.length === 0}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="export-csv-btn"
            >
              {exporting ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Download size={18} className="mr-2" />}
              Exportar CSV
            </Button>
            <Link to="/admin/crm/contacts/new">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" data-testid="add-contact-btn">
                <UserPlus size={18} className="mr-2" />Nuevo Contacto
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
            <Input type="text" placeholder="Buscar por nombre, email, teléfono..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-[#121212] border-white/10 text-white" />
          </form>
          <Select value={filterType} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white">
              <Filter size={16} className="mr-2" /><SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="seller">Vendedores</SelectItem>
              <SelectItem value="buyer">Compradores</SelectItem>
              <SelectItem value="broker">Corredores</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStage} onValueChange={(v) => setFilterStage(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-[#121212] border-white/10 text-white"><SelectValue placeholder="Etapa" /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">Todas</SelectItem>
              {stages.map(s => (<SelectItem key={s.id} value={s.id}><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{backgroundColor:s.color}} />{s.name}</div></SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 bg-[#121212] rounded-lg border border-white/5">
            <Users size={48} className="mx-auto text-[#A3A3A3] mb-4" />
            <p className="text-white text-lg mb-2">No hay contactos</p>
            <p className="text-[#A3A3A3] mb-4">Agrega tu primer contacto</p>
            <Link to="/admin/crm/contacts/new"><Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]"><UserPlus size={18} className="mr-2" />Agregar</Button></Link>
          </div>
        ) : (
          <div className="bg-[#121212] rounded-lg border border-white/5 divide-y divide-white/5">
            {contacts.map((contact) => {
              const stage = getStage(contact.stage_id);
              return (
                <div key={contact.id} className="flex items-center justify-between p-4 hover:bg-[#1a1a1a]">
                  <Link to={`/admin/crm/contacts/${contact.id}`} className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <span className="text-[#D4AF37] font-semibold">{contact.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium hover:text-[#D4AF37]">{contact.name}</p>
                      <div className="flex items-center gap-2 text-sm">
                        {getIcon(contact.contact_type)}
                        <span className="text-[#A3A3A3]">{getLabel(contact.contact_type)}</span>
                        {stage && (<span className="px-2 py-0.5 rounded-full text-xs" style={{backgroundColor:`${stage.color}20`,color:stage.color}}>{stage.name}</span>)}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-4">
                    {contact.phone && <a href={`tel:${contact.phone}`} className="text-[#A3A3A3] hover:text-white flex items-center gap-1"><Phone size={14} />{contact.phone}</a>}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="text-[#A3A3A3] hover:text-white"><MoreVertical size={18} /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
                        <DropdownMenuItem asChild><Link to={`/admin/crm/contacts/${contact.id}`} className="flex items-center gap-2 text-white"><Edit size={14} />Editar</Link></DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(contact.id)} className="flex items-center gap-2 text-red-500"><Trash2 size={14} />Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ContactsList;
